import React, { createContext, FC, useContext, useMemo } from 'react';
import { AnyAction } from 'redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { patchMatrix } from './actions';
import { boardStateReducer, BoardState } from './reducer';
import type { Columns, BoardProps } from './types';

interface BoardStateContext {
  state: BoardState;
  dispatch: React.Dispatch<AnyAction>;
}

const BoardStateContext =
  createContext<BoardStateContext | undefined>(undefined);

const getResult = (cols: Columns, records: any[]): Record<string, string[]> => {
  return cols.reduce((prev, column) => {
    const { dataKey, colId, valueFilterer, value } = column;

    return {
      ...prev,
      [colId]: records
        .filter((task) =>
          valueFilterer
            ? valueFilterer(task[dataKey], task)
            : value === task[dataKey],
        )
        .map(({ taskId }) => taskId) // TODO: pass it as prop
        .map(String),
    };
  }, {});
};

type BoardStateProviderProps = Pick<
  BoardProps,
  'swimlanes' | 'columns' | 'dataSource'
>;

export const BoardStateProvider: FC<BoardStateProviderProps> = ({
  children,
  swimlanes = [],
  columns = [],
  dataSource = [],
}) => {
  const [state, dispatch] = React.useReducer(boardStateReducer, {});

  useDeepCompareEffect(() => {
    if (swimlanes.length > 0) {
      const result = swimlanes.reduce(
        (previousValue, { colId, valueFilterer, dataKey, value }) => {
          const filtered = dataSource.filter((record) =>
            valueFilterer
              ? valueFilterer(record[dataKey], record)
              : record[dataKey] === value,
          );

          return {
            ...previousValue,
            [colId]: getResult(columns, filtered),
          };
        },
        {},
      );

      dispatch(patchMatrix(result));
    } else {
      const result = {
        '-1': getResult(columns, dataSource),
      };

      dispatch(patchMatrix(result));
    }
  }, [dataSource, swimlanes, columns, dispatch]);

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return (
    <BoardStateContext.Provider value={value}>
      {children}
    </BoardStateContext.Provider>
  );
};

export const useBoardContext = (): BoardStateContext => {
  const context = useContext(BoardStateContext);

  if (context === undefined) {
    throw new Error('useBoardContext must be used inside BoardStateProvider');
  }

  return context;
};
